import React, { memo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import AdUnit from '../../../../../../components/AdUnit'
import { htmlParser, SMARTAD } from '../../../../../../modules/parse'
import Encryption from '../../../../../../modules/encryption'
import PlusDialog from './components/PlusDialog'
import GlobalBlurStyle from './components/GlobalBlurStyle'
import ErrorBoundary from '../../../../../Error/widgets/ErrorBoundary'
import translate from '../../../../../../modules/translate'
import consoleLogger from '../../../../../../modules/console-logger'
import TextSkeleton from '../../../../../../components/Text/views/TextSkeleton'
import { useUserContext } from '../../../../../../components/ContextUser'
import { useAdsContext } from '../../../../../../components/ContextAds'
import { translateFontSizeToTheme } from '../../../../../../modules/font'

const StyledAdUnit = styled(AdUnit)`
  > div > div {
    float: right;
    margin-left: ${(props) => props.theme.sizes.gapVerticalHalf};
    margin-bottom: ${(props) => props.theme.sizes.gapVerticalHalf};
    @media (min-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
      margin-right: -169px;
    }
  }
`
const StyledMaxHeightAdUnit = styled(StyledAdUnit)`
  max-height: 400px;
`

const StyledEditor = styled.p`
  margin: 0;
  padding-top: 0;
  padding-bottom: 26px;
  font-family: ${(props) => props.theme.font.primary};
  color: ${(props) => props.theme.color.textSecondary};

  font-size: calc(${(props) => translateFontSizeToTheme(props.fontSize, props.theme.default.fontSize.text)} - 3px);
  @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
    font-size: calc(${(props) => translateFontSizeToTheme(props.fontSize, props.theme.tablet.fontSize.text)} - 3px);
  }
  @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
    font-size: calc(${(props) => translateFontSizeToTheme(props.fontSize, props.theme.mobile.fontSize.text)} - 3px);
  }
`
const StyledMobileAdUnit = styled(AdUnit)`
  > div {
    display: flex;
  }
  > div > div {
    width: calc(100% + ${(props) => props.theme.sizes.gapEdge} + ${(props) => props.theme.sizes.gapEdge});
    justify-content: center;
    margin: 20px 0 0;
    text-align: center;
  }
`
const StyledMaxHeightMobileAdUnit = styled(StyledMobileAdUnit)`
  max-height: 400px;
`
const Wrapper = styled.div`
  margin-bottom: 13px;
  position: relative;
  font-family: ${(props) => props.theme.font.primary};
  color: rgba(0, 0, 0, 0.7);
  line-height: ${(props) => props.theme.lineHeight.text};
  font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.default.fontSize.text)};
  @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
    font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.tablet.fontSize.text)};
  }
  @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
    font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.mobile.fontSize.text)};
  }

  iframe {
    max-width: 100%;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  color: ${(props) => (props.inverted ? props.theme.color.textInverted : 'rgba(0, 0, 0, .7)')};

  h4 {
    font-size: 24px;
    margin: 23px 0;
    @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
      margin: 20px 0;
    }
    @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
      font-size: 20px;
      margin: 21px 0;
    }
  }

  h5 {
    font-size: 22px;
    margin: 25px 0;
    @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
      margin: 20px 0;
    }
    @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
      font-size: 18px;
      margin: 22px 0;
    }
  }

  h6 {
    font-size: 20px;
    margin: 28px 0;
    @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
      margin: 20px 0;
    }
    @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
      font-size: 17px;
      margin: 25px 0;
    }
  }

  a {
    font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.default.fontSize.text)};
    @media (max-width: ${(props) => props.theme.tablet.breakPoint + 'px'}) {
      font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.tablet.fontSize.text)};
    }
    @media (max-width: ${(props) => props.theme.mobile.breakPoint + 'px'}) {
      font-size: ${(props) => translateFontSizeToTheme(props.fontSize, props.theme.mobile.fontSize.text)};
    }

    &:hover {
      opacity: 0.7;
    }
  }

  h4 + br {
    display: none;
  }

  h5 + br {
    display: none;
  }

  h6 + br {
    display: none;
  }

  div.img-inline.inline-file + br {
    display: none;
  }

  .embed-box {
    display: inline-block;
    width: 100%;
  }
`

const PlusTeaser = styled(ContentWrapper)`
  margin-bottom: ${(props) => props.theme.mobile.sizes.gapVertical};
`

const NODECRYPT = 'no-decrypt'

const ContentDetailArticle = ({
  htmlContent,
  plus,
  plusTeaser,
  plusContent,
  embeds,
  adId,
  adTargeting,
  showAds,
  editor,
  fontSize,
  inverted,
  articleLayout,
  isSportnews,
  author,
  ...props
}) => {
  const { adsDispatch } = useAdsContext()
  const [consoleLog] = consoleLogger('ContentDetailArticle')
  const {
    access: { isPlus },
  } = useUserContext()
  consoleLog('plus', isPlus, 'UseAccess')
  const hideContent = plus ? !isPlus && plus : false

  useEffect(() => {
    adsDispatch('ready')
  }, [])

  const content = ({ html, encodedContent, smartAd }) => {
    const parserOptions = {
      embeds,
      fontSize: fontSize,
      dropImageAttribs: true,
      articleLayout,
      adComponents: {
        desktop: <StyledMaxHeightAdUnit adId="S5" mode="tablet_desktop" />,
        mobile: <StyledMaxHeightMobileAdUnit adId="S5" mode="mobile" />,
        showAds: showAds,
        smartAd: smartAd,
        targeting: adTargeting,
      },
    }

    if (encodedContent) {
      const encryption = new Encryption()

      if (hideContent) {
        parserOptions['adComponents']['smartAd'] = SMARTAD.disabled
        parserOptions['scrambleText'] = true
      }
      if (encodedContent !== NODECRYPT) {
        html = encryption.decrypt(encodedContent, process.env.RAZZLE_SPLUS_CONTENT_KEY)
      }
    }
    return htmlParser(html, parserOptions)
  }

  let encodedContent = plusContent
  if (hideContent && !plusContent) encodedContent = NODECRYPT

  const errorOptions = {
    component: translate('article'),
    link: '/',
    linkText: translate('error_try_homepage'),
    logError: 'Content Detail Article',
  }

  return (
    <ErrorBoundary {...errorOptions}>
      <Wrapper className="content" fontSize={fontSize} {...props}>
        <GlobalBlurStyle />
        {(!plusContent || isPlus !== undefined) && (
          <>
            {hideContent && plusTeaser && (
              <PlusTeaser id="plusTeaser" inverted={inverted}>
                {content({ html: plusTeaser + '<br>', smartAd: SMARTAD.disabled })}
              </PlusTeaser>
            )}
            {hideContent && <PlusDialog id="plusDialog" />}
            {!hideContent && (
              <ContentWrapper
                id="articleContent"
                className={hideContent && 'blur'}
                inverted={inverted}
                fontSize={fontSize}
              >
                {content({ html: htmlContent, encodedContent: encodedContent })}
              </ContentWrapper>
            )}
          </>
        )}
        {plusContent && isPlus === undefined && (
          <>
            <TextSkeleton lines={5} fontSize="16px" lastWidth="45px" justify={false} />
            <TextSkeleton lines={3} fontSize="16px" lastWidth="65px" justify={false} />
          </>
        )}
      </Wrapper>
      {editor && !isSportnews && !hideContent && !author && <StyledEditor fontSize={fontSize}>{editor}</StyledEditor>}
    </ErrorBoundary>
  )
}

ContentDetailArticle.propTypes = {
  htmlContent: PropTypes.string,
  plusTeaser: PropTypes.string,
  plusContent: PropTypes.string,
  embeds: PropTypes.object,
  adId: PropTypes.string,
  plus: PropTypes.bool,
}

ContentDetailArticle.defaultProps = {
  plusTeaser: null,
  plusContent: null,
}

const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.fontSize === nextProps.fontSize
}

export default memo(ContentDetailArticle, propsAreEqual)
